/**
 * Transition banner — schema v2 layer composer (backend owns overlap timing).
 *
 * v2 is extended additively; every new field is optional and its default
 * reproduces the old behaviour, so designs saved earlier render the same.
 *   config: { schemaVersion, transitionType, holdSec, transitionSec,
 *             motion, motionAmount, layers[] }
 *   layer:  { id, name, type: fill|gradient|image|text|shape, visible, locked,
 *             opacity, zIndex, xPct, yPct, widthPct, heightPct, anchor,
 *             rotation, ...type props }
 */

export const BANNER_SCHEMA_VERSION = 2;

export const BANNER_TIMING = {
  holdSec: { min: 0.3, max: 4, default: 0.8 },
  transitionSec: { min: 0.3, max: 1.5, default: 0.85 },
};

export const BANNER_MOTIONS = [
  { value: 'none', label: 'Still' },
  { value: 'zoom_in', label: 'Zoom in' },
  { value: 'zoom_out', label: 'Zoom out' },
  { value: 'pan_left', label: 'Pan left' },
  { value: 'pan_right', label: 'Pan right' },
];

/** Fonts the render server (Windows) has — preview uses the same faces. */
export const BANNER_FONTS = [
  { value: 'Impact', label: 'Impact', css: 'Impact, "Arial Black", sans-serif' },
  { value: 'Arial Black', label: 'Arial Black', css: '"Arial Black", Arial, sans-serif' },
  { value: 'Arial', label: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { value: 'Segoe UI', label: 'Segoe UI', css: '"Segoe UI", Arial, sans-serif' },
  { value: 'Verdana', label: 'Verdana', css: 'Verdana, sans-serif' },
  { value: 'Tahoma', label: 'Tahoma', css: 'Tahoma, Verdana, sans-serif' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS', css: '"Trebuchet MS", sans-serif' },
  { value: 'Calibri', label: 'Calibri', css: 'Calibri, Arial, sans-serif' },
  { value: 'Georgia', label: 'Georgia', css: 'Georgia, serif' },
  { value: 'Times New Roman', label: 'Times New Roman', css: '"Times New Roman", Times, serif' },
  { value: 'Courier New', label: 'Courier New', css: '"Courier New", monospace' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS', css: '"Comic Sans MS", cursive' },
];

/** First family in a CSS stack, without quotes ("Arial Black, Impact" → "Arial Black"). */
export function primaryFontFamily(fontFamily) {
  const first = String(fontFamily || 'Impact').split(',')[0].trim().replace(/^["']|["']$/g, '');
  return first || 'Impact';
}

export function fontCss(fontFamily) {
  const fam = primaryFontFamily(fontFamily);
  const known = BANNER_FONTS.find((f) => f.value.toLowerCase() === fam.toLowerCase());
  if (known) return known.css;
  return String(fontFamily || '').includes(',') ? fontFamily : `"${fam}", Arial, sans-serif`;
}

/* ── Circle images ─────────────────────────────────────────────── */
/** Perfect circle — 50% of min(width,height); use with shape: 'circle' + sizePct. */
export const CIRCLE_IMAGE_SHAPE = 'circle';
export const CIRCLE_IMAGE_RADIUS = 0.5;

export function isCircleImageLayer(layer) {
  return layer?.type === 'image' && layer?.shape === CIRCLE_IMAGE_SHAPE;
}

/** Pixel diameter from sizePct (% of min canvas edge). */
export function circlePixelSize(dims, sizePct) {
  const pct = Number(sizePct);
  if (!dims || !Number.isFinite(pct)) return 0;
  return Math.max(2, Math.min(dims.w, dims.h) * (pct / 100));
}

export function syncCircleLayerForCanvas(layer, dims) {
  if (!isCircleImageLayer(layer) || !dims) return layer;
  const sizePct = layer.sizePct ?? layer.widthPct ?? 28;
  const px = circlePixelSize(dims, sizePct);
  return {
    ...layer,
    shape: CIRCLE_IMAGE_SHAPE,
    sizePct,
    widthPct: (px / dims.w) * 100,
    heightPct: (px / dims.h) * 100,
    borderRadiusPct: CIRCLE_IMAGE_RADIUS,
  };
}

export function createCircleImageLayer(overrides = {}) {
  const base = createLayer('image', {
    shape: CIRCLE_IMAGE_SHAPE,
    sizePct: 40,
    widthPct: 40,
    heightPct: 40,
    borderRadiusPct: CIRCLE_IMAGE_RADIUS,
    objectFit: 'cover',
    xPct: 50,
    yPct: 44,
    anchor: 'center',
    borderWidth: 4,
    borderColor: '#ffffff',
    name: 'Circle image',
    ...overrides,
  });
  return {
    ...base,
    shape: CIRCLE_IMAGE_SHAPE,
    borderRadiusPct: CIRCLE_IMAGE_RADIUS,
  };
}

/* ── Transitions ───────────────────────────────────────────────── */
export const FFMPEG_TRANSITIONS = [
  { id: 'swipe_left', label: 'Swipe Left', description: 'New content pushes in from the right', ffmpeg: 'slideleft' },
  { id: 'swipe_right', label: 'Swipe Right', description: 'Push from the left', ffmpeg: 'slideright' },
  { id: 'swipe_up', label: 'Swipe Up', description: 'Vertical push upward', ffmpeg: 'slideup' },
  { id: 'swipe_down', label: 'Swipe Down', description: 'Vertical push downward', ffmpeg: 'slidedown' },
  { id: 'wipe_left', label: 'Wipe Left', description: 'Hard edge wipe', ffmpeg: 'wipeleft' },
  { id: 'wipe_right', label: 'Wipe Right', description: 'Hard edge wipe', ffmpeg: 'wiperight' },
  { id: 'fade', label: 'Cross Fade', description: 'Smooth dissolve', ffmpeg: 'fade' },
  { id: 'fade_black', label: 'Dip to Black', description: 'Brief black bridge', ffmpeg: 'fadeblack' },
  { id: 'fade_white', label: 'Dip to White', description: 'Brief white bridge', ffmpeg: 'fadewhite' },
  { id: 'circle_open', label: 'Circle Open', description: 'Iris reveal', ffmpeg: 'circleopen' },
  { id: 'circle_close', label: 'Circle Close', description: 'Iris close', ffmpeg: 'circleclose' },
  { id: 'pixelize', label: 'Pixelize', description: 'Block dissolve', ffmpeg: 'pixelize' },
  { id: 'radial', label: 'Radial', description: 'Clock-wipe style', ffmpeg: 'radial' },
  { id: 'smooth_left', label: 'Smooth Left', description: 'Soft horizontal blend', ffmpeg: 'smoothleft' },
  { id: 'zoom_blur', label: 'Zoom Blur', description: 'Zoom cross (ffmpeg zoom+fade composite)', ffmpeg: 'zoom_fade' },
];

export const TRANSITION_GROUPS = [
  { group: 'Swipe', ids: ['swipe_left', 'swipe_right', 'swipe_up', 'swipe_down'] },
  { group: 'Wipe', ids: ['wipe_left', 'wipe_right', 'smooth_left'] },
  { group: 'Fade', ids: ['fade', 'fade_black', 'fade_white', 'zoom_blur'] },
  { group: 'Shape', ids: ['circle_open', 'circle_close', 'radial', 'pixelize'] },
];

/* ── Layers ────────────────────────────────────────────────────── */
let _layerSeq = 0;

export function newLayerId(prefix = 'layer') {
  _layerSeq += 1;
  return `${prefix}_${_layerSeq}_${Math.random().toString(36).slice(2, 6)}`;
}

export const LAYER_TYPE_LABELS = {
  fill: 'Solid fill',
  gradient: 'Gradient',
  image: 'Image',
  text: 'Text',
  shape: 'Shape',
};

export const TEXT_DEFAULTS = {
  text: 'YOUR TEXT',
  color: '#ffffff',
  fontFamily: 'Arial Black',
  fontWeight: '800',
  fontSizePct: 0.065,
  letterSpacing: 0.04, // ×0.1em
  lineHeight: 1.15,
  textAlign: 'center',
  textTransform: 'uppercase',
  // shadow: on by default (the editor preview always showed one)
  shadowEnabled: true,
  shadowColor: '#000000',
  shadowOpacity: 0.45,
  shadowBlurPct: 0.011, // of canvas height
  shadowOffsetXPct: 0,
  shadowOffsetYPct: 0.004,
  strokeWidthPct: 0, // of canvas height
  strokeColor: '#000000',
  bgEnabled: false,
  bgColor: '#000000',
  bgOpacity: 0.6,
  bgRadiusPct: 0.2,
  paddingPct: 0.004, // of canvas height
};

export function createLayer(kind, overrides = {}) {
  const base = {
    id: newLayerId(kind),
    visible: true,
    locked: false,
    opacity: 1,
    zIndex: 0,
    xPct: 0,
    yPct: 0,
    widthPct: 100,
    heightPct: 100,
    anchor: 'top-left',
    rotation: 0,
  };

  if (kind === 'fill') {
    return { ...base, type: 'fill', color: '#0f172a', ...overrides };
  }

  if (kind === 'gradient') {
    return {
      ...base,
      type: 'gradient',
      gradientType: 'linear',
      gradientFrom: '#7c3aed',
      gradientTo: '#2563eb',
      gradientAngle: 135,
      ...overrides,
    };
  }

  if (kind === 'image') {
    return {
      ...base,
      type: 'image',
      imageUrl: '',
      objectFit: 'cover',
      borderRadiusPct: 0,
      imageFocusXPct: 50,
      imageFocusYPct: 50,
      borderWidth: 0,
      borderColor: '#ffffff',
      xPct: 50,
      yPct: 50,
      widthPct: 28,
      heightPct: 28,
      anchor: 'center',
      ...overrides,
    };
  }

  if (kind === 'text') {
    return {
      ...base,
      type: 'text',
      ...TEXT_DEFAULTS,
      xPct: 50,
      yPct: 78,
      widthPct: 90,
      heightPct: 12,
      anchor: 'center',
      ...overrides,
    };
  }

  if (kind === 'shape') {
    return {
      ...base,
      type: 'shape',
      shapeKind: 'rect', // rect | ellipse
      fillEnabled: true,
      color: '#7c3aed',
      strokeEnabled: false,
      strokeColor: '#ffffff',
      strokeWidthPct: 0.004,
      borderRadiusPct: 0,
      xPct: 50,
      yPct: 50,
      widthPct: 30,
      heightPct: 20,
      anchor: 'center',
      ...overrides,
    };
  }

  return { ...base, type: kind, ...overrides };
}

export function layerLabel(layer) {
  if (!layer) return '';
  if (layer.name) return layer.name;
  if (isCircleImageLayer(layer)) return 'Circle image';
  if (layer.type === 'text') {
    const t = String(layer.text || '').trim().split('\n')[0];
    return t ? (t.length > 22 ? `${t.slice(0, 22)}…` : t) : 'Text';
  }
  if (layer.type === 'shape') return layer.shapeKind === 'ellipse' ? 'Ellipse' : 'Rectangle';
  return LAYER_TYPE_LABELS[layer.type] || layer.type;
}

export function defaultBannerConfig(overrides = {}) {
  return {
    schemaVersion: BANNER_SCHEMA_VERSION,
    transitionType: 'swipe_left',
    holdSec: BANNER_TIMING.holdSec.default,
    transitionSec: BANNER_TIMING.transitionSec.default,
    motion: 'none',
    motionAmount: 0.08,
    layers: [
      createLayer('gradient', { zIndex: 0, gradientFrom: '#7c3aed', gradientTo: '#2563eb' }),
      createLayer('text', { zIndex: 10, text: 'UP NEXT', yPct: 72, fontSizePct: 0.07 }),
    ],
    ...overrides,
  };
}

export const DEFAULT_BANNER_CONFIG = defaultBannerConfig();

function migrateLegacyConfig(legacy) {
  const layers = [];
  let z = 0;

  if (legacy.backgroundType === 'image' && legacy.backgroundImageUrl) {
    layers.push(
      createLayer('image', {
        zIndex: z++,
        imageUrl: legacy.backgroundImageUrl,
        objectFit: legacy.backgroundImageFit || 'cover',
        xPct: 0,
        yPct: 0,
        widthPct: 100,
        heightPct: 100,
        anchor: 'top-left',
        borderRadiusPct: 0,
      }),
    );
    if (legacy.backgroundOverlayOpacity > 0) {
      layers.push(
        createLayer('fill', {
          zIndex: z++,
          color: legacy.backgroundOverlayColor || '#000000',
          opacity: legacy.backgroundOverlayOpacity,
        }),
      );
    }
  } else if (legacy.backgroundType === 'gradient') {
    layers.push(
      createLayer('gradient', {
        zIndex: z++,
        gradientFrom: legacy.gradientFrom,
        gradientTo: legacy.gradientTo,
        gradientAngle: legacy.gradientAngle,
      }),
    );
  } else {
    layers.push(createLayer('fill', { zIndex: z++, color: legacy.backgroundColor || '#0f172a' }));
  }

  if (legacy.showLogo && legacy.logoUrl) {
    layers.push(
      createLayer('image', {
        zIndex: z++,
        imageUrl: legacy.logoUrl,
        xPct: 50,
        yPct: 45,
        widthPct: (legacy.logoWidthPct || 0.2) * 100,
        heightPct: (legacy.logoMaxHeightPct || 0.35) * 100,
        borderRadiusPct: legacy.logoBorderRadiusPct || 0,
        objectFit: 'contain',
        anchor: 'center',
      }),
    );
  }

  if (legacy.headline) {
    layers.push(
      createLayer('text', {
        zIndex: z++,
        text: legacy.headline,
        color: legacy.headlineColor,
        fontSizePct: legacy.headlineSizePct,
        yPct: 72,
      }),
    );
  }

  if (legacy.subtext) {
    layers.push(
      createLayer('text', {
        zIndex: z++,
        text: legacy.subtext,
        color: legacy.subtextColor,
        fontSizePct: legacy.subtextSizePct,
        yPct: 82,
        fontWeight: '500',
        textTransform: 'none',
      }),
    );
  }

  const swipeMap = { left: 'swipe_left', right: 'swipe_right', up: 'swipe_up', down: 'swipe_down' };

  return defaultBannerConfig({
    transitionType: swipeMap[legacy.swipeDirection] || 'swipe_left',
    layers: layers.length ? layers : DEFAULT_BANNER_CONFIG.layers,
  });
}

const clampNum = (v, lo, hi, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
};

export function normalizeBannerConfig(raw) {
  if (!raw || typeof raw !== 'object') return defaultBannerConfig();
  if (raw.schemaVersion === BANNER_SCHEMA_VERSION && Array.isArray(raw.layers)) {
    return {
      schemaVersion: BANNER_SCHEMA_VERSION,
      transitionType: raw.transitionType || 'swipe_left',
      holdSec: clampNum(raw.holdSec, BANNER_TIMING.holdSec.min, BANNER_TIMING.holdSec.max, BANNER_TIMING.holdSec.default),
      transitionSec: clampNum(
        raw.transitionSec,
        BANNER_TIMING.transitionSec.min,
        BANNER_TIMING.transitionSec.max,
        BANNER_TIMING.transitionSec.default,
      ),
      motion: BANNER_MOTIONS.some((m) => m.value === raw.motion) ? raw.motion : 'none',
      motionAmount: clampNum(raw.motionAmount, 0.02, 0.3, 0.08),
      layers: raw.layers.map((l) => {
        const layer = { ...l, id: l.id || newLayerId(l.type) };
        if (isCircleImageLayer(layer)) {
          layer.borderRadiusPct = CIRCLE_IMAGE_RADIUS;
          layer.shape = CIRCLE_IMAGE_SHAPE;
        }
        return layer;
      }),
    };
  }
  return migrateLegacyConfig(raw);
}

/* ── Geometry (shared by canvas, inspector and export) ─────────── */
/** Layer box in canvas px: centre, size, rotation (deg, clockwise). */
export function layerBox(layer, dims) {
  let w;
  let h;
  if (isCircleImageLayer(layer)) {
    w = h = circlePixelSize(dims, layer.sizePct ?? layer.widthPct ?? 28);
  } else {
    w = Math.max(1, dims.w * ((layer.widthPct ?? 100) / 100));
    h = Math.max(1, dims.h * ((layer.heightPct ?? 100) / 100));
  }
  const px = dims.w * ((layer.xPct ?? 0) / 100);
  const py = dims.h * ((layer.yPct ?? 0) / 100);
  const centered = layer.anchor === 'center';
  return {
    cx: centered ? px : px + w / 2,
    cy: centered ? py : py + h / 2,
    w,
    h,
    rot: Number(layer.rotation) || 0,
  };
}

/** Write a px box back to the layer (switches it to centre anchor). */
export function applyLayerBox(layer, box, dims) {
  const next = {
    ...layer,
    anchor: 'center',
    xPct: (box.cx / dims.w) * 100,
    yPct: (box.cy / dims.h) * 100,
    rotation: Math.round((box.rot ?? layer.rotation ?? 0) * 10) / 10,
  };
  if (isCircleImageLayer(layer)) {
    const side = Math.max(box.w, box.h);
    next.sizePct = (side / Math.min(dims.w, dims.h)) * 100;
    return syncCircleLayerForCanvas(next, dims);
  }
  next.widthPct = (box.w / dims.w) * 100;
  next.heightPct = (box.h / dims.h) * 100;
  return next;
}

export function previewDimsForOrientation(orientation) {
  return orientation === 'portrait' ? { w: 1080, h: 1920 } : { w: 1920, h: 1080 };
}

export function normalizeBannerConfigForExport(raw, orientation = 'landscape') {
  const cfg = normalizeBannerConfig(raw);
  const dims = previewDimsForOrientation(orientation);
  return {
    ...cfg,
    layers: cfg.layers.map((l) => syncCircleLayerForCanvas(l, dims)),
  };
}

/* ── Presets ───────────────────────────────────────────────────── */
export const BANNER_PRESETS = [
  {
    id: 'blank',
    label: 'Blank Canvas',
    description: 'Start empty — add your own layers',
    config: defaultBannerConfig({
      layers: [createLayer('fill', { zIndex: 0, color: '#0f172a', name: 'Background' })],
      transitionType: 'fade',
    }),
  },
  {
    id: 'photo_hero',
    label: 'Photo Hero',
    description: 'Full-bleed image + dark scrim + headline',
    config: defaultBannerConfig({
      transitionType: 'swipe_left',
      motion: 'zoom_in',
      layers: [
        createLayer('image', {
          zIndex: 0, imageUrl: '', objectFit: 'cover', xPct: 0, yPct: 0, widthPct: 100, heightPct: 100, anchor: 'top-left', name: 'Photo',
        }),
        createLayer('fill', { zIndex: 1, color: '#000000', opacity: 0.45, name: 'Scrim' }),
        createLayer('text', { zIndex: 2, text: 'WATCH THIS', yPct: 75, fontSizePct: 0.075 }),
      ],
    }),
  },
  {
    id: 'circle_hero',
    label: 'Circle Hero',
    description: 'Perfect circle image — stays round at any size',
    config: defaultBannerConfig({
      transitionType: 'circle_open',
      layers: [
        createLayer('gradient', { zIndex: 0, gradientFrom: '#0f172a', gradientTo: '#1e3a5f', gradientAngle: 160 }),
        createCircleImageLayer({ zIndex: 1, sizePct: 42, xPct: 50, yPct: 46, borderWidth: 5, borderColor: '#ffffff' }),
        createLayer('text', { zIndex: 2, text: 'UP NEXT', yPct: 78, fontSizePct: 0.07 }),
      ],
    }),
  },
  {
    id: 'logo_card',
    label: 'Logo Card',
    description: 'Gradient + rounded logo + optional text',
    config: defaultBannerConfig({
      transitionType: 'circle_open',
      layers: [
        createLayer('gradient', { zIndex: 0, gradientFrom: '#1e1b4b', gradientTo: '#312e81' }),
        createLayer('image', {
          zIndex: 1, imageUrl: '', xPct: 50, yPct: 46, widthPct: 32, heightPct: 32, borderRadiusPct: 0.18, objectFit: 'cover', anchor: 'center', name: 'Logo',
        }),
        createLayer('text', { zIndex: 2, text: '', yPct: 78, fontSizePct: 0.055 }),
      ],
    }),
  },
  {
    id: 'split_panel',
    label: 'Split Panel',
    description: 'Color block + side image strip',
    config: defaultBannerConfig({
      transitionType: 'wipe_right',
      layers: [
        createLayer('fill', { zIndex: 0, color: '#111827' }),
        createLayer('image', {
          zIndex: 1, imageUrl: '', xPct: 72, yPct: 50, widthPct: 52, heightPct: 100, borderRadiusPct: 0.06, objectFit: 'cover', anchor: 'center',
        }),
        createLayer('text', {
          zIndex: 2, text: 'UP NEXT', xPct: 28, yPct: 50, widthPct: 44, textAlign: 'left', fontSizePct: 0.08, anchor: 'center',
        }),
      ],
    }),
  },
  {
    id: 'minimal_fade',
    label: 'Minimal Fade',
    description: 'Soft background image, subtle text',
    config: defaultBannerConfig({
      transitionType: 'fade',
      layers: [
        createLayer('image', {
          zIndex: 0, imageUrl: '', objectFit: 'cover', xPct: 0, yPct: 0, widthPct: 100, heightPct: 100, anchor: 'top-left',
        }),
        createLayer('fill', { zIndex: 1, color: '#000000', opacity: 0.25 }),
        createLayer('text', {
          zIndex: 2, text: '—', yPct: 50, fontSizePct: 0.05, textTransform: 'none', fontWeight: '600',
        }),
      ],
    }),
  },
  {
    id: 'news_flash',
    label: 'News Flash',
    description: 'Red bar, bold label and headline',
    config: defaultBannerConfig({
      transitionType: 'wipe_left',
      holdSec: 1.1,
      layers: [
        createLayer('fill', { zIndex: 0, color: '#0b0b0f', name: 'Background' }),
        createLayer('shape', {
          zIndex: 1, color: '#e11d2e', xPct: 50, yPct: 56, widthPct: 100, heightPct: 22, name: 'Red bar',
        }),
        createLayer('shape', {
          zIndex: 2, color: '#ffffff', xPct: 18, yPct: 38, widthPct: 22, heightPct: 10, name: 'Label box',
        }),
        createLayer('text', {
          zIndex: 3, text: 'BREAKING', color: '#e11d2e', fontFamily: 'Arial Black', xPct: 18, yPct: 38, widthPct: 22, heightPct: 10, fontSizePct: 0.05, shadowEnabled: false,
        }),
        createLayer('text', {
          zIndex: 4, text: 'UP NEXT: THE FULL STORY', fontFamily: 'Impact', xPct: 50, yPct: 56, widthPct: 92, heightPct: 20, fontSizePct: 0.085, letterSpacing: 0.3,
        }),
      ],
    }),
  },
  {
    id: 'bold_type',
    label: 'Bold Type',
    description: 'Huge outlined headline on a glow',
    config: defaultBannerConfig({
      transitionType: 'zoom_blur',
      motion: 'zoom_out',
      layers: [
        createLayer('gradient', {
          zIndex: 0, gradientType: 'radial', gradientFrom: '#4c1d95', gradientTo: '#09090b', name: 'Glow',
        }),
        createLayer('text', {
          zIndex: 1, text: "WHAT\nHAPPENED\nNEXT?", fontFamily: 'Impact', xPct: 50, yPct: 50, widthPct: 90, heightPct: 80, fontSizePct: 0.15, lineHeight: 0.95,
          color: '#fde047', strokeWidthPct: 0.006, strokeColor: '#000000', letterSpacing: 0.2,
        }),
      ],
    }),
  },
  {
    id: 'channel_card',
    label: 'Channel Card',
    description: 'Circle logo, channel name and tagline',
    config: defaultBannerConfig({
      transitionType: 'circle_open',
      holdSec: 1.2,
      layers: [
        createLayer('gradient', { zIndex: 0, gradientFrom: '#0f172a', gradientTo: '#312e81', gradientAngle: 120 }),
        createCircleImageLayer({ zIndex: 1, sizePct: 34, xPct: 50, yPct: 38, borderWidth: 6, name: 'Channel logo' }),
        createLayer('text', {
          zIndex: 2, text: 'YOUR CHANNEL', xPct: 50, yPct: 70, widthPct: 90, heightPct: 12, fontSizePct: 0.075,
        }),
        createLayer('text', {
          zIndex: 3, text: 'Stories worth your time', fontFamily: 'Segoe UI', fontWeight: '400', textTransform: 'none', xPct: 50, yPct: 80, widthPct: 80, heightPct: 8, fontSizePct: 0.04, color: '#c7d2fe',
        }),
      ],
    }),
  },
  {
    id: 'lower_card',
    label: 'Lower Card',
    description: 'Photo with a rounded caption card',
    config: defaultBannerConfig({
      transitionType: 'swipe_up',
      motion: 'pan_right',
      layers: [
        createLayer('image', {
          zIndex: 0, imageUrl: '', objectFit: 'cover', xPct: 0, yPct: 0, widthPct: 100, heightPct: 100, anchor: 'top-left', name: 'Photo',
        }),
        createLayer('text', {
          zIndex: 1, text: 'Coming up next', fontFamily: 'Segoe UI', fontWeight: '700', textTransform: 'none', xPct: 30, yPct: 80, widthPct: 48, heightPct: 14, fontSizePct: 0.055,
          bgEnabled: true, bgColor: '#111827', bgOpacity: 0.85, bgRadiusPct: 0.3, textAlign: 'center', shadowEnabled: false,
        }),
      ],
    }),
  },
];

export function getBannerPresetById(id) {
  return BANNER_PRESETS.find((p) => p.id === id) || BANNER_PRESETS[0];
}

export function getTransitionMeta(id) {
  return FFMPEG_TRANSITIONS.find((t) => t.id === id) || FFMPEG_TRANSITIONS[0];
}
